import React, { useState, useEffect } from 'react';
import { ThemeProvider } from 'styled-components';
import Header from './components/header/header';
import Sidebar from './components/sidebar/sidebar';
import { GlobalStyles } from './globals';
import { lightTheme } from './themes/light';
import { darkTheme } from './themes/dark';
import { LanguageProvider } from './contexts/LanguageContext';
import { ErrorBoundary } from './components/ErrorBoundary';

// Usar uma união dos tipos de temas
type AppTheme = typeof lightTheme | typeof darkTheme;

const Layout: React.FC<{children: React.ReactNode}> = ({ children }) => {
  /*
    Tema CLARO, com o degradê de neutros Hexium — o mesmo tratamento aplicado ao webwidget, para as
    duas telas lerem como um produto só.

    A troca só foi segura DEPOIS de migrar as ~340 cores literais para tokens. Na ordem inversa o
    resultado seria uma aplicação inutilizável: boa parte daqueles literais era branco e cinza-claro
    escritos para fundo escuro, e num fundo claro viram branco sobre branco. O tema escuro segue
    disponível e completo em `darkTheme` — trocar de volta é mudar esta linha.
  */
  const theme: AppTheme = lightTheme;

  // Estado para controlar se o sidebar está colapsado
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    // No mobile, sempre começar com sidebar colapsado
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
    if (isMobile) {
      return true;
    }
    
    // No desktop, usar estado salvo
    const savedState = localStorage.getItem('sidebarCollapsed');
    return savedState === 'true';
  });

  // Salva no localStorage sempre que o estado do sidebar mudar
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', isSidebarCollapsed.toString());
  }, [isSidebarCollapsed]);

  // Detecta mudanças de tamanho de tela para ajustar o sidebar
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (isMobile) {
        // No mobile, sempre colapsar o sidebar quando redimensiona
        setIsSidebarCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Função para alternar o estado do sidebar
  const handleToggleSidebar = () => {
    setIsSidebarCollapsed(prev => !prev);
  };
  
  return (
    <LanguageProvider>
      <ThemeProvider theme={theme as any}>
        <ErrorBoundary>
          <GlobalStyles />
          <div className="root-layout">
            <Header onToggleSidebar={handleToggleSidebar} />
            <div className="content-container">
              <Sidebar 
                activeItem="dashboard" 
                isCollapsed={isSidebarCollapsed}
                onToggleCollapse={handleToggleSidebar}
              />
              <main className="main-content" data-sidebar-collapsed={isSidebarCollapsed}>
                {children}
              </main>
            </div>
          </div>
        </ErrorBoundary>
      </ThemeProvider>
    </LanguageProvider>
  );
};

export default Layout;